import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { TextField, Paper, InputAdornment } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { QboLink, QboTypography, QboHighlighter } from '@ui/Components';
import { SearchOutlined as SearchOutlinedIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { trim } from '@utilities/stringUtils';

function PaperComponent({
  children,
  loading,
  linkText,
  onClickLink,
  options,
  searchValue,
  minimumInputLength,
  ...rest
}) {
  const { t } = useTranslation();

  if (minimumInputLength && searchValue.length <= minimumInputLength) {
    return (
      <Paper {...rest} className={`QboAutocomplete__popper-wrapper ${rest.className}`}>
        <QboTypography className="QboAutocomplete__minimum-keyword">
          {t('label.minimum_characters')}
        </QboTypography>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Paper {...rest} className={`QboAutocomplete__popper-wrapper ${rest.className}`}>
        <QboTypography className="QboAutocomplete__loading-text">
          {t('label.loading')}...
        </QboTypography>
      </Paper>
    );
  }

  if (options && options.length === 0) return null;

  return (
    <Paper {...rest} className={`QboAutocomplete__popper-wrapper ${rest.className}`}>
      <div>{children}</div>
      {linkText && searchValue.length > 0 && (
        <QboLink
          onMouseDown={(e) => {
            e.preventDefault();
            onClickLink();
          }}
          className="QboAutocomplete__bottom-link">
          {linkText}
        </QboLink>
      )}
    </Paper>
  );
}

PaperComponent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  onClickLink: PropTypes.func,
  linkText: PropTypes.string,
  loading: PropTypes.bool,
  options: PropTypes.array,
  searchValue: PropTypes.string,
  minimumInputLength: PropTypes.number,
};

PaperComponent.defaultProps = {
  children: null,
  linkText: null,
  onClickLink: () => {},
  loading: false,
  options: [],
  searchValue: '',
  minimumInputLength: 0,
};

export default function QboAutocomplete({
  keywordFieldRef,
  searchResultLabel,
  onChange,
  onClickLink,
  placeholder,
  loading,
  options,
  keyGroupBy,
  keyLabelOption,
  onSelect,
  onInputChange,
  clearOnEscape,
  clearOnBlur,
  defaultSearchValue,
  forceShowClearIcon,
  disabled,
  minimumInputLength,
  ...rest
}) {
  const [searchValue, setSearchValue] = useState(defaultSearchValue);
  const searchValueTrim = trim(searchValue);

  return (
    <Autocomplete
      {...rest}
      className={`QboAutocomplete__wrapper ${
        forceShowClearIcon ? 'QboAutocomplete__always-show-icon' : ''
      } `}
      freeSolo
      inputValue={searchValue}
      clearOnEscape={clearOnEscape}
      clearOnBlur={clearOnBlur}
      blurOnSelect
      options={options.sort((a, b) => -b[keyGroupBy].localeCompare(a[keyGroupBy]))}
      groupBy={(option) => option[keyGroupBy]}
      getOptionLabel={(option) => option[keyLabelOption]}
      // eslint-disable-next-line no-shadow
      filterOptions={(options, state) => {
        const trimmedInput = trim(state.inputValue).toLowerCase();

        return options.filter((option) => {
          const label = option[keyLabelOption]?.toLowerCase() || '';
          return label.includes(trimmedInput);
        });
      }}
      renderOption={(props, option, state) => {
        return (
          <li {...props} key={option?.id}>
            <QboTypography>
              {state.inputValue !== '' ? (
                <QboHighlighter
                  searchWord={trim(state.inputValue)}
                  sentences={option[keyLabelOption]}
                />
              ) : (
                option[keyLabelOption]
              )}
            </QboTypography>
          </li>
        );
      }}
      clearIcon={<CancelIcon />}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            onChange={onChange}
            ref={keywordFieldRef}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              className: `${params.InputProps.className} FormControl__default-wrapper`,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
              onKeyDown: (e) => {
                const hasEnoughInput = minimumInputLength
                  ? searchValueTrim.length > minimumInputLength
                  : searchValueTrim.length > 0;
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  if (onClickLink && hasEnoughInput) onClickLink();
                }
              },
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              ...params.inputProps,
              maxLength: 100,
            }}
          />
        );
      }}
      PaperComponent={PaperComponent}
      componentsProps={{
        paper: {
          linkText: searchResultLabel,
          loading,
          onClickLink,
          options,
          searchValue: searchValueTrim,
          minimumInputLength,
        },
      }}
      onChange={(e, value) => {
        onSelect(value);
      }}
      onInputChange={(e, value, reason) => {
        if (reason !== 'reset') {
          setSearchValue(value);
          onInputChange(value);
        }
      }}
      disabled={disabled}
    />
  );
}

QboAutocomplete.propTypes = {
  placeholder: PropTypes.string,
  keywordFieldRef: PropTypes.object,
  onChange: PropTypes.func,
  searchResultLabel: PropTypes.string,
  onClickLink: PropTypes.func,
  loading: PropTypes.bool,
  options: PropTypes.array,
  keyGroupBy: PropTypes.string,
  keyLabelOption: PropTypes.string,
  onSelect: PropTypes.func,
  onInputChange: PropTypes.func,
  clearOnEscape: PropTypes.bool,
  clearOnBlur: PropTypes.bool,
  defaultSearchValue: PropTypes.string,
  forceShowClearIcon: PropTypes.bool,
  disabled: PropTypes.bool,
  minimumInputLength: PropTypes.number,
};

QboAutocomplete.defaultProps = {
  placeholder: '',
  keywordFieldRef: null,
  onChange: () => {},
  searchResultLabel: null,
  onClickLink: () => {},
  loading: false,
  options: [],
  keyGroupBy: '',
  keyLabelOption: '',
  onSelect: () => {},
  onInputChange: () => {},
  clearOnEscape: true,
  clearOnBlur: true,
  defaultSearchValue: '',
  forceShowClearIcon: false,
  disabled: false,
  minimumInputLength: 0,
};
